import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Copy, Clock, Search } from "lucide-react";
import { toast } from "react-toastify";
import axios from "axios";

const API_BASE_URL = `${process.env.REACT_APP_API_URL?.replace(/\/$/, "") || ""}/api`;

const CopyTravelDialog = ({ isOpen, onClose, travelData, users, fetchData }) => {
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [travelDate, setTravelDate] = useState(new Date());

  useEffect(() => {
    if (isOpen && travelData) {
      const date = new Date(travelData.start_date || travelData.planned_date_start);
      setTravelDate(date);
    }
  }, [isOpen, travelData]);

  const filteredUsers = users.filter((user) =>
    `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCopyTravel = async () => {
    if (!selectedUser || !travelData) {
      toast.error("Please select a user first");
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem("access_token");
      const originalStartDate = new Date(travelData.start_date || travelData.planned_date_start);
      const originalEndDate = new Date(travelData.end_date || travelData.planned_date_end);
      const newStartDate = new Date(travelDate);
      newStartDate.setHours(originalStartDate.getHours(), originalStartDate.getMinutes(), 0, 0);
      const newEndDate = new Date(travelDate);
      newEndDate.setHours(originalEndDate.getHours(), originalEndDate.getMinutes(), 0, 0);

      const newTravelData = {
        user: selectedUser,
        start_date: newStartDate.toISOString(),
        end_date: newEndDate.toISOString(),
        address: travelData.address,
        description: travelData.description || travelData.address,
      };

      await axios.post(
        `${API_BASE_URL}/travel/`,
        newTravelData,
        {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        }
      );

      toast.success("Travel copied successfully");
      onClose();
      fetchData();
    } catch (error) {
      console.error("Error copying travel:", error);
      toast.error("Failed to copy travel");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Copy className="h-5 w-5 mr-2 text-green-600" />
            <span>Copy Travel to Another User</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 my-4">
          <div className="bg-green-50 p-3 rounded-lg border border-green-200">
            <h3 className="text-sm font-medium text-green-800 mb-1">Travel Details</h3>
            <p className="text-xs text-green-700 font-medium">
              {travelData?.address || travelData?.description || "Unnamed travel"}
            </p>
            {travelData && (
              <div className="text-xs text-gray-600 mt-1 flex items-center">
                <Clock className="h-3 w-3 mr-1 text-gray-500" />
                <span>
                  {new Date(travelData.start_date || travelData.planned_date_start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
                  {" - "}
                  {new Date(travelData.end_date || travelData.planned_date_end).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })}
                </span>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Travel Date</label>
            <input
              type="date"
              className="w-full p-2 text-sm border rounded"
              value={travelDate.toISOString().split("T")[0]}
              onChange={(e) => setTravelDate(new Date(e.target.value))}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Search User</label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-2.5 text-gray-400" />
              <input
                type="text"
                className="w-full p-2 pl-9 text-sm border rounded"
                placeholder="Type name to filter users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Select User</label>
            <div className="max-h-48 overflow-y-auto border rounded">
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <div
                    key={user.id}
                    className={`p-2 text-sm cursor-pointer hover:bg-green-50 ${selectedUser === user.id ? "bg-green-100" : ""}`}
                    onClick={() => setSelectedUser(user.id)}
                  >
                    <div className={`w-3 h-3 rounded-full mr-2 inline-block ${selectedUser === user.id ? "bg-green-500" : "border border-gray-300"}`}></div>
                    {user.first_name} {user.last_name}
                  </div>
                ))
              ) : (
                <div className="p-3 text-center text-sm text-gray-500">No users found</div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleCopyTravel}
            disabled={loading || !selectedUser}
            className="bg-green-600 hover:bg-green-700 text-white"
          >
            {loading ? "Copying..." : "Copy Travel"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CopyTravelDialog;